export type ValueType = 'standard' | 'arbitrary';

/**
 * Supported editor breakpoints.
 * - 'base': Mobile/default styles (no prefix in Tailwind)
 * - 'sm': Small screens (sm: prefix in Tailwind)
 * - 'md': Medium screens (md: prefix in Tailwind)
 * - 'lg': Large screens (lg: prefix in Tailwind)
 * - 'xl': Extra large screens (xl: prefix in Tailwind)
 * - '2xl': 2X large screens (2xl: prefix in Tailwind)
 */
export const EDITOR_BREAKPOINTS = ['base', 'sm', 'md', 'lg', 'xl', '2xl'] as const;
export type EditorBreakpoint = (typeof EDITOR_BREAKPOINTS)[number];

/**
 * Properties that support breakpoint-aware editing in the editor.
 * These properties can have different values for mobile (base) vs desktop (lg).
 */
const BREAKPOINT_AWARE_PROPERTIES = [
  // Typography
  'fontSize',

  // Display
  'display',
  'gridCols',

  // Layout
  'flexDirection',
  'alignItems',
  'alignSelf',
  'justifyContent',
  'flexWrap',
  'gap',

  // Size
  'width',
  'height',
  'minWidth',
  'maxWidth',
  'minHeight',
  'maxHeight',

  // Spacing - Padding
  'padding',
  'paddingX',
  'paddingY',
  'paddingTop',
  'paddingBottom',
  'paddingLeft',
  'paddingRight',

  // Spacing - Margin
  'margin',
  'marginX',
  'marginY',
  'marginTop',
  'marginBottom',
  'marginLeft',
  'marginRight',
] as const;

type BreakpointAwareProperty = (typeof BREAKPOINT_AWARE_PROPERTIES)[number];

/**
 * Check if a property supports breakpoint-aware editing.
 */
export function isBreakpointAwareProperty(property: string): property is BreakpointAwareProperty {
  return BREAKPOINT_AWARE_PROPERTIES.includes(property as BreakpointAwareProperty);
}

export interface ParsedValue {
  type: ValueType;
  property: string;
  value: string;
  breakpoint: EditorBreakpoint;
  normalizedValue?: string;
  modifier?: string;
}

export type PropertyValue = Partial<Record<EditorBreakpoint, ParsedValue>>;

export interface ParsedClasses {
  [property: string]: PropertyValue | string[];
  unknown: string[];
}

/**
 * Type guard to check if a value from ParsedClasses is a PropertyValue.
 * Use this when accessing dynamic properties from ParsedClasses to safely narrow the type.
 *
 * @example
 * const propertyValue = parsed[property];
 * if (isPropertyValue(propertyValue)) {
 *   // propertyValue is now typed as PropertyValue
 *   const baseValue = propertyValue.base;
 * }
 */
export function isPropertyValue(value: unknown): value is PropertyValue {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
